import {
  BasicPermission,
  ResourcePermission,
} from '@mainstage/plugin-permission-common';

export enum TeamRoleEnum {
  OWNER = 'OWNER',
  DATA_ACCESS_MANAGER = 'DATA_ACCESS_MANAGER',
  DEVELOPER = 'DEVELOPER',
  LIFECYCLE_MANAGER = 'LIFECYCLE_MANAGER',
}

export enum PracticeShaperRelationEnum {
  IS_OWNED_BY = 'isOwnedBy',
  DATA_ACCESS_GRANTED_BY = 'dataAccessGrantedBy',
  IS_DEVELOPED_BY = 'isDevelopedBy',
  LIFECYCLE_MANAGED_BY = 'lifecycleManagedBy',
}

export type TeamRole = {
  /**
   * The unique identifier of this Team Role. This is used to reference the Team Role in the code and should not be changed once set. For example, the Owner Team Role has the name "owner".
   */
  name: TeamRoleEnum;

  /**
   * A user-friendly name to display in the UI when referring to this Team Role. For example, the Owner Team Role has the display name "Owner".
   */
  displayName: string;

  /**
   * A description of the responsibilities and capabilities of this Team Role. This is used to provide more context to users when they are assigning team roles in their projects, so it should be as descriptive as possible.
   */
  description: string;
  /**
   * RBAC permission that identifies who has this Team Role as primary assignee. For example, the Owner of a project is identified by having the RBAC permission control-plane.project.team-roles.manage
   */
  assigneeRbacPermission: BasicPermission | ResourcePermission;
  /**
   * RBAC permission that identifies who has this Team Role as limited assignee. For example, the limited assignee of Owner of a project is identified by having the RBAC permission control-plane.project.team-roles.limited-manage
   */
  limitedAssigneeRbacPermission: BasicPermission | ResourcePermission;
  /**
   * The keyword used to link this Team Role to a Practice Shaper entity. For example, the Owner Team Role is linked to the isOwnedBy relation in Practice Shaper.
   */
  practiceShaperRelation: PracticeShaperRelationEnum;
};

export type TeamRolesSubject = {
  subject: string;
  isLimited: boolean;
  exactMatch: boolean;
  isFallback: boolean;
  fallbackType?: 'catalog-info-owner' | 'assigned-owner';
};

export type TeamRolesResolveResponse = {
  subjects: TeamRolesSubject[];
  total: number;
  isFeatureEnabled: boolean;
  isRequestedRoleEnabled: boolean;
};

export type EnabledTeamRoleInfo = {
  name: TeamRoleEnum;
  displayName: string;
  description: string;
  canHaveLimitedAssignees: boolean;
  assigneeRbacRole: string;
  assigneeKind?: 'user' | 'group';
  limitedAssigneeRbacRole?: string;
  limitedAssigneeKind?: 'user' | 'group';
};

export type TeamRolesConfigurationResponse = {
  enabled: boolean;
  teamRoles: EnabledTeamRoleInfo[];
};
